import React, { useEffect, useState } from "react";
import useOrganization from "../organization/hooks/useOrganization";
import { getSurveyScores } from "./api/individualResults.api";
import getAverage from "../../utils/getAverageScore";
import BackgroundCircles from "../../components/ui/BackgroundCircles";

// import IntroIcon1 from "../../components/ui/pngicons/intro-item-1.png";
import IntroIcon2 from "../../components/ui/pngicons/intro-item-2.png";
import IntroIcon3 from "../../components/ui/pngicons/intro-item-3.png";
import PageContent from "../../app/layouts/PageContent";
import { useProfile } from "../auth/hooks/useProfile";
import RapidTrustRadar from "./pages/RapidTrustRadar";
import TeamSpiderGraph from "./pages/TeamSpiderGraph";
import StressCircle from "./pages/StressCircle";
import RapidTrustInsights from "./pages/RapidTrustInsights";
import StressInsightsSection from "./pages/StressInsightsSection";
import IndividualResultsFooter from "./pages/IndividualResultsFooter";
import PrintIndividualResults from "./ReportPrint/PrintIndividualResults";
import { Navigate, useLocation } from "react-router-dom";
import useSurvey from "../survey/hooks/useSurvey";
import {
  getTeamCompositeScores,
  getTeamMemberScores,
  getUserSurveyScores,
} from "../TeamcompositeResults/api/team.api";
import type { TeamMemberScore } from "../TeamcompositeResults/api/team.api";
import { useTeam } from "../TeamcompositeResults/contexts/TeamContext";
import { useCoach } from "../coach/contexts/CoachProvider";

const SectionDivider = () => (
  <div className="bg-white py-12">
    <div className="mx-auto max-w-[1155px] px-4 sm:px-6 pt-10 sm:pt-14 pb-10 sm:pb-14">
      <div className="border-b border-[#efefef] pb-14"></div>
    </div>
  </div>
);

export const IndividualResults: React.FC = () => {
  document.title = "Individual results";
  const { currentOrganization } = useOrganization();

  const [scores, setScores] = useState<any>({});
  const [teamMembers, setTeamMembers] = useState<TeamMemberScore[]>([]);
  const { user, isCoach } = useProfile();
  const location = useLocation();
  const selectedUser = location.state?.user;
  const { selectedTeam } = useTeam();
  const clickedTeamId = location.state?.teamId;
  const clickedTeamName = location.state?.teamName;
  const { defaultSurveyId: coachSurveyId } = useCoach();
  const { isNewSurvey } = useSurvey();

  // Own results are stale while a retake is queued; team/other-user views are unaffected.
  const isOwnResults = !selectedUser?.id && !clickedTeamId;


  const questionnaireId = isCoach
    ? coachSurveyId
    : currentOrganization?.default_survey;

  useEffect(() => {
    if (!questionnaireId || questionnaireId <= 0) return;
    if (isNewSurvey && isOwnResults) return;

    const loadScores = async () => {
  try {
    let res;

    if (selectedUser?.id) {
      res = await getUserSurveyScores({
        questionnaire_id: Number(questionnaireId),
        user_id: Number(selectedUser.id),
      });

    } else if (clickedTeamId) {
      // ✅ ONLY use team when explicitly clicked
      res = await getTeamCompositeScores({
        questionnaire_id: Number(questionnaireId),
        team_id: Number(clickedTeamId),
      });

      const membersRes = await getTeamMemberScores({
        questionnaire_id: Number(questionnaireId),
        team_id: Number(clickedTeamId),
        limit: 15,
      });
      if (membersRes.success) {
        setTeamMembers(membersRes.data?.members ?? []);
      }
    } else {
      // ✅ ALWAYS fallback to individual
      res = await getSurveyScores(Number(questionnaireId));
    }
        const scores = res.data?.scores || res.data || {};

        setScores(scores);
      } catch (err) {
        console.error("Score loading error:", err);
      }
    };

    loadScores();
  }, [
    questionnaireId,
    selectedUser,
    selectedTeam,
    clickedTeamId,
    isNewSurvey,
    isOwnResults,
  ]);

//   useEffect(() => {
//   if (Object.keys(scores).length > 0) {
//     setIsReadyToPrint(true);
//   }
// }, [scores]);

 const handlePrint = () => {
  setTimeout(() => {
    window.print();
  }, 2000); // allow render time
};
  const stressValues = {
    Arguing: +getAverage(scores.Arguing).toFixed(0),
    Freezing: +getAverage(scores.Freezing).toFixed(0),
    Retreating: +getAverage(scores.Retreating).toFixed(0),
    Audacious: +getAverage(scores.Audacious).toFixed(0),
    Appeasing: +getAverage(scores.Appeasing).toFixed(0),
  };

  const trustValues = {
    Independence: Math.round(getAverage(scores.Independence)),
    Advancement: Math.round(getAverage(scores.Advancement)),
    Exploring: Math.round(getAverage(scores.Exploring)),
    Caring: Math.round(getAverage(scores.Caring)),
    Collaboration: Math.round(getAverage(scores.Collaboration)),
    Safety: Math.round(getAverage(scores.Safety)),
  };
  const isTeamView = !!clickedTeamId;
  const displayName = selectedUser?.id
    ? `${selectedUser.first_name} ${selectedUser.last_name}`
    : clickedTeamId
      ? `${clickedTeamName} Team`
      : `${user?.first_name} ${user?.last_name}`;

  const radarValues = Object.values(trustValues);

  const sortedTrust = Object.entries(trustValues)
    .map(([label, value]) => ({ label, value }))
    .sort((a: any, b: any) => b.value - a.value);

  const primary = sortedTrust[0];
  const secondary = sortedTrust[1];

  if (isNewSurvey && isOwnResults) {
    return <Navigate to="/locker-room" replace />;
  }

  if (!questionnaireId) return null;

  return (
    <>
      <div className="print:hidden">
        <PageContent>
            <section
              className="relative bg-gradient-to-b from-[#dfe9ff] via-[#eef3ff] to-white 
px-6 sm:px-10 lg:px-24 xl:px-36 
py-16 sm:py-20 lg:py-28 
rounded-2xl"
            >
              <BackgroundCircles />

              <div className="relative z-10 flex flex-col lg:flex-row gap-10 lg:gap-16">
                {/* LEFT TEXT SECTION */}

                <div className="w-full lg:max-w-[720px]">
                  <h2 className="text-[22px] font-semibold text-[#26295e] mb-20 relative">
                    {isTeamView ? "Team Results" : "Individual Results"}
                    <span className="absolute -bottom-4 left-0 w-16 h-[3px] bg-[#7365FF]" />
                  </h2>

                  <h1
                    className="text-2xl sm:text-3xl lg:text-[40px] 
font-bold text-[#2a2e6e] 
leading-tight lg:leading-[48px] 
mb-10 lg:mb-20"
                  >
                    {displayName}, Team Trust Accelerator Results
                  </h1>

                  <p className="text-[#7d86a9] text-[16px] mb-12">
                    Within this report you’ll find:
                  </p>

                  <div className="space-y-10 text-[15px] leading-relaxed text-[#6b7394]">
                    {/* <div className="flex items-start gap-4 sm:gap-6">
                      <img
                        src={IntroIcon1}
                        alt=""
                        className="w-10 h-10 sm:w-14 sm:h-14 mt-1"
                      />
                      <p>
                        Your team style preference: relational or structural.
                        Every team needs individuals who have a relational,
                        people-orientation and a structural,
                        process-orientation. This report will identify which
                        style is more comfortable to you.
                      </p>
                    </div> */}

                    <div className="flex gap-6">
                      <img src={IntroIcon2} alt="" className="w-14 h-14 mt-1" />
                      <p>
                        Social Needs for Rapid Trust Building: everyone has a
                        different set of social needs that must be met to build
                        trust with a team. This report will identify which
                        social needs are most important to you.
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <img src={IntroIcon3} alt="" className="w-14 h-14 mt-1" />
                      <p>
                        Unprofessional Stress Response: when under stress,
                        threat, or pressure, everyone has a response that feels
                        unprofessional to teammates. This report will identify
                        your stress responses that are most likely to harm your
                        team’s performance.
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handlePrint}
                    className="mt-14 px-10 py-4 bg-[#3b519c] text-white rounded-xl shadow-lg"
                  >
                    Print Results
                  </button>
                </div>

                {/* RIGHT CHART SECTION */}

                <div className="flex flex-col gap-6 w-full lg:w-[380px]">
                  {/* RAPID TRUST CARD */}

                  <div className="bg-white rounded-2xl shadow-[0_18px_40px_rgba(0,0,0,0.08)] p-4 sm:p-6">
                    <h3 className="text-[14px] font-semibold text-[#26295e] mb-3">
                      Rapid Trust Build
                      <span className="block mt-1 w-8 h-[2px] bg-[#7365FF]" />
                    </h3>

                    <div className="flex justify-center">
                      {isTeamView ? (
                        <TeamSpiderGraph apiMembers={teamMembers} />
                      ) : (
                        <RapidTrustRadar data={radarValues} interactive={true} />
                      )}
                    </div>

                    <div className="mt-3 space-y-2">
                      {[primary, secondary].map((item, index) => (
                        <div key={index}>
                          <div className="flex justify-between text-[12px] text-[#26295e] mb-1">
                            <span>{item.label}</span>
                            <span>{item.value}%</span>
                          </div>

                          <div className="h-[4px] bg-[#e6eaf6] rounded-full relative">
                            <div
                              className="h-[4px] bg-[#2f2a68] rounded-full"
                              style={{ width: `${item.value}%` }}
                            />

                            <div
                              className="absolute -top-[3px] w-[9px] h-[9px] rounded-full bg-[#2f2a68]"
                              style={{ left: `calc(${item.value}% - 4px)` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* STRESS RESPONSE CARD */}

                  <div className="bg-white rounded-2xl shadow-[0_18px_40px_rgba(0,0,0,0.08)] p-4">
                    <h3 className="text-[14px] font-semibold text-[#26295e] mb-4">
                      Unprofessional Stress Response
                      <span className="block mt-1 w-8 h-[2px] bg-[#7365FF]" />
                    </h3>

                    <div className="space-y-4">
                      <div className="flex justify-between flex-wrap gap-4">
                        <StressCircle
                          label="Arguing"
                          value={stressValues.Arguing}
                          color="#F4A261"
                          size={75}
                        />
                        <StressCircle
                          label="Freezing"
                          value={stressValues.Freezing}
                          color="#9AA1B5"
                          size={65}
                        />
                      </div>

                      <div className="flex justify-center">
                        <StressCircle
                          label="Retreating"
                          value={stressValues.Retreating}
                          color="#9AA1B5"
                          size={70}
                        />
                      </div>

                      <div className="flex justify-between items-end">
                        <StressCircle
                          label="Audacious"
                          value={stressValues.Audacious}
                          color="#E63946"
                          size={90}
                        />
                        <StressCircle
                          label="Appeasing"
                          value={stressValues.Appeasing}
                          color="#9AA1B5"
                          size={70}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <SectionDivider />
            <RapidTrustInsights />
            <SectionDivider />
            <StressInsightsSection />
            <SectionDivider />
            <IndividualResultsFooter />
        </PageContent>
      </div>
      {/* kept off-screen (not display:none) so ApexCharts can initialise with real dimensions */}
      <div className="print-root fixed -left-[9999px] top-0 w-[794px] print:relative print:left-0 print:top-auto print:w-full">
  <PrintIndividualResults teamMembers={teamMembers} />
</div>
    </>
  );
};

export default IndividualResults;
